import React, { useState, useEffect } from 'react';
import Layout from '../../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { psAPI } from '../../../services/api';
import { toast } from 'sonner';
import { 
  DollarSign, FileText, Settings, ArrowRight, 
  TrendingUp, CheckCircle, AlertCircle, Clock 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function PSFIDashboard() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await psAPI.getPSFIDashboard();
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching PS-FI dashboard:', error);
      toast.error('Failed to load PS-FI dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
        </div>
      </Layout>
    );
  }

  const stats = [
    {
      title: 'Account Determination Rules',
      value: dashboard?.account_determination_rules || 0,
      icon: Settings,
      color: 'bg-orange-500',
      link: '/ps/integration/account-determination'
    },
    {
      title: 'FI Documents This Period',
      value: Object.values(dashboard?.documents_by_type || {}).reduce((a, b) => a + b, 0),
      icon: FileText,
      color: 'bg-green-500',
      link: '/ps/integration/fi-documents'
    },
    {
      title: 'Projects Pending Settlement',
      value: dashboard?.projects_pending_settlement || 0,
      icon: Clock,
      color: 'bg-orange-500',
      link: '/ps/closing/settlement'
    },
    {
      title: 'Projects Pending WIP',
      value: dashboard?.projects_pending_wip || 0,
      icon: TrendingUp,
      color: 'bg-orange-500',
      link: '/ps/closing/wip'
    }
  ];

  const quickActions = [
    {
      title: 'Account Determination',
      description: 'Configure GL account mappings for PS transactions',
      icon: Settings,
      link: '/ps/integration/account-determination'
    },
    {
      title: 'FI Documents',
      description: 'View and manage PS-FI posting documents',
      icon: FileText,
      link: '/ps/integration/fi-documents'
    },
    {
      title: 'WIP Posting',
      description: 'Post Work-in-Progress to Finance',
      icon: TrendingUp,
      link: '/ps/integration/wip-posting'
    }
  ];

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="ps-fi-dashboard">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">PS-FI Integration</h1>
            <p className="text-gray-500 mt-1">
              Fiscal Year {dashboard?.current_fiscal_year} / Period {dashboard?.current_period}
            </p>
          </div>
          <Button onClick={fetchDashboard} variant="outline" data-testid="refresh-btn">
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat) => (
            <Card 
              key={stat.label || stat.title || stat.id} 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(stat.link)}
              data-testid={`stat-card-${index}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">{stat.title}</p>
                    <p className="text-2xl font-bold mt-1">{stat.value}</p>
                  </div>
                  <div className={`${stat.color} p-3 rounded-lg`}>
                    <stat.icon className="h-6 w-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Financial Summary */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-green-600" />
                Financial Summary (Current Period)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-orange-50 rounded-lg p-4">
                  <p className="text-sm text-orange-600 font-medium">Project Costs Posted</p>
                  <p className="text-xl font-bold text-orange-900 mt-1">
                    ${(dashboard?.total_project_costs_posted || 0).toLocaleString()}
                  </p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-green-600 font-medium">Settlement Posted</p>
                  <p className="text-xl font-bold text-green-900 mt-1">
                    ${(dashboard?.total_settlement_posted || 0).toLocaleString()}
                  </p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4">
                  <p className="text-sm text-orange-600 font-medium">WIP Posted</p>
                  <p className="text-xl font-bold text-orange-900 mt-1">
                    ${(dashboard?.total_wip_posted || 0).toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Documents by Type */}
              {Object.keys(dashboard?.documents_by_type || {}).length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-gray-600 mb-3">Documents by Type</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(dashboard?.documents_by_type || {}).map(([type, count]) => (
                      <Badge key={type} variant="outline" className="py-1 px-3">
                        {type}: {count}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {quickActions.map((action) => (
                <Button
                  key={action.label || action.title || action.path}
                  variant="outline"
                  className="w-full justify-between h-auto py-3"
                  onClick={() => navigate(action.link)}
                  data-testid={`quick-action-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <action.icon className="h-5 w-5 text-gray-500" />
                    <div className="text-left">
                      <p className="font-medium">{action.title}</p>
                      <p className="text-xs text-gray-500">{action.description}</p>
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-gray-400" />
                </Button>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Recent Documents */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5 text-orange-600" />
              Recent FI Documents
            </CardTitle>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/ps/integration/fi-documents')}
            >
              View All
            </Button>
          </CardHeader>
          <CardContent>
            {(dashboard?.recent_documents || []).length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p>No FI documents created yet</p>
                <p className="text-sm mt-1">Documents will appear here after posting</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 font-medium">Document #</th>
                      <th className="text-left p-3 font-medium">Type</th>
                      <th className="text-left p-3 font-medium">Project</th>
                      <th className="text-right p-3 font-medium">Amount</th>
                      <th className="text-left p-3 font-medium">Status</th>
                      <th className="text-left p-3 font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboard.recent_documents.map((doc) => (
                      <tr key={doc.fi_document_id} className="border-b hover:bg-gray-50">
                        <td className="p-3 font-mono text-orange-600">{doc.document_number}</td>
                        <td className="p-3">
                          <Badge variant="outline">{doc.document_type}</Badge>
                        </td>
                        <td className="p-3">{doc.project_name || doc.project_id}</td>
                        <td className="p-3 text-right font-medium">
                          ${doc.total_debit?.toLocaleString() || '0'}
                        </td>
                        <td className="p-3">
                          {doc.reversed ? (
                            <Badge className="bg-red-100 text-red-800">Reversed</Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-800">Posted</Badge>
                          )}
                        </td>
                        <td className="p-3 text-gray-500">
                          {new Date(doc.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
